/** Starter stub — EMS fee installment helpers (platform template). */

function toMoneyNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundMoney(value) {
  return Math.round(toMoneyNumber(value) * 100) / 100;
}

/** Split net amount across installments; last row absorbs remainder. */
export function getExpectedInstallmentAmount(netAmount, count, installmentNumber) {
  const total = toMoneyNumber(netAmount);
  const n = Number(count);
  const idx = Number(installmentNumber);
  if (!Number.isFinite(n) || n < 1 || !Number.isFinite(idx) || idx < 1) {
    return null;
  }
  const base = roundMoney(total / n);
  if (idx < n) return base;
  return roundMoney(total - base * (n - 1));
}

export function getFeeInstallmentCapacityError() {
  return null;
}

export function moneyMatches(a, b, epsilon = 0.01) {
  return Math.abs(toMoneyNumber(a) - toMoneyNumber(b)) <= epsilon;
}

/** Returns error string or null when amounts are acceptable. */
export function validateFeeInstallmentAmounts() {
  return null;
}

export function sumInstallmentAmounts(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return list.reduce(
    (acc, row) => {
      acc.inr = roundMoney(acc.inr + toMoneyNumber(row?.fee_instalment_inr));
      acc.student = roundMoney(
        acc.student + toMoneyNumber(row?.fee_instalment_student_student_currency)
      );
      return acc;
    },
    { inr: 0, student: 0 }
  );
}

/**
 * Build / resize installment rows for admissions forms.
 * Preserves existing row fields when preserveAmounts is true.
 */
export function buildFeeInstallmentRows({
  netInr,
  netStudent,
  count,
  currencyCode = "",
  existingRows = [],
  preserveAmounts = false,
} = {}) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 1) return [];

  const existing = Array.isArray(existingRows) ? existingRows : [];

  return Array.from({ length: n }, (_, i) => {
    const installmentNumber = i + 1;
    const prev = existing[i] || {};
    const expectedInr = getExpectedInstallmentAmount(netInr, n, installmentNumber);
    const expectedStudent = getExpectedInstallmentAmount(
      netStudent,
      n,
      installmentNumber
    );

    return {
      ...prev,
      installment_number: installmentNumber,
      due_date: prev.due_date || "",
      fee_instalment_inr:
        preserveAmounts && prev.fee_instalment_inr != null && prev.fee_instalment_inr !== ""
          ? prev.fee_instalment_inr
          : expectedInr != null
            ? String(expectedInr)
            : "",
      currency_code_inr: prev.currency_code_inr || "INR",
      fee_instalment_student_student_currency:
        preserveAmounts &&
        prev.fee_instalment_student_student_currency != null &&
        prev.fee_instalment_student_student_currency !== ""
          ? prev.fee_instalment_student_student_currency
          : expectedStudent != null
            ? String(expectedStudent)
            : "",
      student_currency_code: prev.student_currency_code || currencyCode || "",
    };
  });
}

/** Whether an admission still has room for another installment. */
export function canCreateMoreFeeInstallments(admission, allInstallments = []) {
  const admissionId =
    admission?.organization_ems_admission_id ?? admission?.id ?? null;
  if (admissionId == null) return true;

  const allowed = Number(admission?.installment_count);
  if (!Number.isFinite(allowed) || allowed < 1) return true;

  const list = Array.isArray(allInstallments) ? allInstallments : [];
  const used = list.filter((row) =>
    String(row?.organization_ems_admission_id ?? "") === String(admissionId)
  ).length;

  return used < allowed;
}
